import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import DeliveryDiningRoundedIcon from '@mui/icons-material/DeliveryDiningRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import { useAuth } from '../auth/AuthContext';
import PageMenuButton from '../components/PageMenuButton';
import './Avaliacoes.css';

const API_URL = getApiUrl();
const REVIEW_POLICY_URL = 'https://portal.ifood.com.br/evaluation-policies';

const statusOptions = [
  { value: 'NOT_REPLIED', label: 'Não respondida' },
  { value: 'PUBLISHED', label: 'Concluída' },
  { value: 'REPLIED', label: 'Resposta enviada' },
];

const scoreOptions = [
  { value: '5', label: '5' },
  { value: '4', label: '4' },
  { value: '3', label: '3' },
  { value: '2', label: '2' },
  { value: '1', label: '1' },
];

const commentOptions = [
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' },
];

const orderOptions = [
  { value: 'CREATED_AT', label: 'Avaliação' },
  { value: 'ORDER_DATE', label: 'Pedido' },
];

const toneOptions = [
  { value: 'cordial', label: 'Cordial' },
  { value: 'objetivo', label: 'Objetivo' },
  { value: 'acolhedor', label: 'Acolhedor' },
  { value: 'formal', label: 'Formal' },
  { value: 'profissional', label: 'Profissional' },
];

const defaultAgentConfig = {
  enabled: false,
  autoReplyEnabled: false,
  selectedTemplateId: 'default-cordial',
  customInstructions: '',
  tone: 'cordial',
  maxChars: 300,
  replyWithoutComment: true,
  autoReplyOnlyStoreReviews: true,
  maxRepliesPerRun: 3,
  riskKeywords: [],
  updatedAt: null,
  updatedBy: null,
};

const pad = (value) => String(value).padStart(2, '0');

const toDateInput = (date) => {
  const safeDate = date instanceof Date ? date : new Date(date);
  return `${safeDate.getFullYear()}-${pad(safeDate.getMonth() + 1)}-${pad(safeDate.getDate())}`;
};

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const initialFilters = () => {
  const now = new Date();
  return {
    sortBy: 'CREATED_AT',
    sort: 'DESC',
    dateFrom: toDateInput(addDays(now, -89)),
    dateTo: toDateInput(now),
    score: '',
    status: '',
    hasComments: '',
    orderId: '',
  };
};

const normalizeMerchantName = (merchant = {}) => (
  merchant.displayName ||
  merchant.name ||
  merchant.tradingName ||
  merchant.corporateName ||
  merchant.shortName ||
  merchant.id ||
  'Loja iFood'
);

const normalizeMerchantList = (payload) => {
  const list = Array.isArray(payload) ? payload : payload?.merchants || [];
  return list
    .map((merchant) => {
      const id = String(merchant?.id || merchant?.merchantId || '').trim();
      if (!id) return null;
      return {
        ...merchant,
        id,
        displayName: normalizeMerchantName(merchant),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'pt-BR'));
};

const toIsoStart = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  return Number.isFinite(date.getTime()) ? date.toISOString().replace(/\.\d{3}Z$/, 'Z') : '';
};

const toIsoEnd = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T23:59:59`);
  return Number.isFinite(date.getTime()) ? date.toISOString().replace(/\.\d{3}Z$/, 'Z') : '';
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatScore = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return '--';
  return numeric.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const getReviewScore = (review = {}) => {
  const numeric = Number(review.score);
  return Number.isFinite(numeric) ? numeric : null;
};

const getReviewOrderShortId = (review = {}) => (
  review.order?.shortId ||
  review.order?.displayId ||
  review.order?.id ||
  '-'
);

const getReviewStatusLabel = (status) => {
  const normalized = String(status || '').toUpperCase();
  if (normalized === 'NOT_REPLIED') return 'Aguardando resposta';
  if (normalized === 'REPLIED') return 'Respondida';
  if (normalized === 'PUBLISHED') return 'Concluída';
  if (normalized === 'INVALID' || normalized === 'DISCARDED') return 'Irregular';
  if (normalized === 'CREATED') return 'Em análise';
  return normalized || '-';
};

const getReviewStatusClassName = (status) => {
  const normalized = String(status || '').toUpperCase();
  if (normalized === 'PUBLISHED') return 'is-success';
  if (normalized === 'NOT_REPLIED') return 'is-warning';
  if (normalized === 'INVALID' || normalized === 'DISCARDED') return 'is-danger';
  return '';
};

const buildDistribution = (reviews = []) => {
  const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  reviews.forEach((review) => {
    const score = Math.round(Number(review.score));
    if (score >= 1 && score <= 5) {
      distribution[score] += 1;
    }
  });
  return distribution;
};

const calculateAverage = (reviews = []) => {
  const scores = reviews.map(getReviewScore).filter((value) => Number.isFinite(value));
  if (!scores.length) return null;
  return scores.reduce((sum, score) => sum + score, 0) / scores.length;
};

const getMerchantReply = (review = {}) => (
  (Array.isArray(review.replies) ? review.replies : [])
    .filter((reply) => String(reply?.from || '').toUpperCase() === 'MERCHANT')
    .sort((left, right) => new Date(right.createdAt || 0) - new Date(left.createdAt || 0))[0] || null
);

const canEditRecentReply = (review = {}) => {
  const reply = getMerchantReply(review);
  if (!reply?.createdAt) return false;
  const createdAt = new Date(reply.createdAt).getTime();
  if (!Number.isFinite(createdAt)) return false;
  return Date.now() - createdAt <= 10 * 60 * 1000;
};

const canAnswerReview = (review = {}) => (
  String(review.status || '').toUpperCase() === 'NOT_REPLIED' ||
  canEditRecentReply(review)
);

const normalizeAgentConfig = (config = {}) => ({
  ...defaultAgentConfig,
  ...(config && typeof config === 'object' ? config : {}),
  riskKeywords: Array.isArray(config?.riskKeywords) ? config.riskKeywords : [],
});

const canManageAgentConfig = (user = {}) => (
  ['programador', 'administrador', 'gerente'].includes(String(user.role || '').trim().toLowerCase())
);

const agentStatusLabels = {
  answered: 'Respondida',
  'human-review': 'Atenção humana',
  skipped: 'Ignorada',
  error: 'Erro',
  saved: 'Configuração salva',
  completed: 'Ciclo concluído',
  'answer-error': 'Falha ao enviar',
};

const agentActionLabels = {
  answer: 'Resposta automática',
  'ai-answer': 'Resposta de IA',
  'ai-answer-manual': 'Resposta assistida por IA',
  'manual-answer': 'Resposta manual',
  'human-review': 'Atenção humana',
  'human-review-answer': 'Revisão humana respondida',
  'ai-generate': 'Geração da IA',
  detail: 'Detalhe da avaliação',
  list: 'Consulta de avaliações',
  skip: 'Avaliação ignorada',
  config: 'Configuração',
  'manual-run': 'Execução manual',
  access: 'Acesso iFood',
  'cycle-summary': 'Relatório do ciclo',
};

const agentBlockerLabels = {
  openai_api_key_missing: 'OPENAI_API_KEY ausente no backend',
  scheduler_not_started: 'Scheduler automático não iniciou',
  agent_disabled: 'Agente ativo desligado',
  auto_reply_disabled: 'Responder Automaticamente desligado',
  ifood_access_check_failed: 'Falha ao validar acesso iFood',
  ifood_access_denied: 'Acesso iFood negado',
  'ifood-credentials-missing': 'Credenciais iFood ausentes',
  'ifood-merchant-ids-missing': 'Nenhuma loja iFood configurada',
  'ifood-merchant-owned-by-another-company': 'Lojas iFood vinculadas a outro tenant',
};

const getAgentLogStatusLabel = (log = {}) => (
  log.statusLabel ||
  agentStatusLabels[log.status] ||
  agentActionLabels[log.action] ||
  log.status ||
  log.action ||
  'Evento'
);

const getAgentLogMerchantName = (log = {}, merchants = []) => {
  const merchantId = String(log.merchantId || '').trim();
  const fromLog = String(log.merchantName || '').trim();
  if (fromLog && fromLog !== merchantId) return fromLog;
  return merchants.find((merchant) => merchant.id === merchantId)?.displayName || 'Loja iFood';
};

const canOpenAgentLogReview = (log = {}) => Boolean(log.canOpenReview || (log.merchantId && log.reviewId));

const isHumanReviewLog = (log = {}) => (
  Boolean(log.requiresHumanAction) ||
  String(log.status || '').trim() === 'human-review' ||
  String(log.action || '').trim() === 'human-review'
);

const formatAgentBlocker = (value) => (
  agentBlockerLabels[String(value || '').trim()] || String(value || '').trim() || 'Bloqueio não identificado'
);

const formatAgentRunSummary = (summary = {}) => {
  if (!summary || typeof summary !== 'object') return '';
  const parts = [
    `${Number(summary.answered || 0)} respondida(s)`,
    `${Number(summary.humanReview || 0)} revisão humana`,
    `${Number(summary.errors || 0)} erro(s)`,
    `${Number(summary.merchantsScanned || 0)} loja(s)`,
    `${Number(summary.pagesScanned || 0)} página(s)`,
  ];
  if (summary.reason) parts.push(formatAgentBlocker(summary.reason));
  return parts.join(' • ');
};

const buildReviewsUrl = (merchantId, filters, section, extra = {}) => {
  const shouldHydrateDetails = section === 'logistics' || extra.hydrateDetails;
  const params = new URLSearchParams({
    page: String(extra.page || 1),
    pageSize: String(extra.pageSize || (shouldHydrateDetails ? 10 : 50)),
    addCount: 'true',
    sort: filters.sort || 'DESC',
    sortBy: filters.sortBy || 'CREATED_AT',
  });

  const dateFrom = toIsoStart(filters.dateFrom);
  const dateTo = toIsoEnd(filters.dateTo);
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  if (filters.score) params.set('score', filters.score);
  if (filters.status) params.set('status', filters.status);
  if (filters.hasComments) params.set('hasComments', filters.hasComments);
  if (filters.orderId) params.set('orderId', filters.orderId);
  if (section === 'logistics') params.set('section', section);
  if (shouldHydrateDetails) params.set('hydrateDetails', 'true');

  return `${API_URL}/api/ifood/merchants/${merchantId}/reviews?${params.toString()}`;
};

function StarRating({ value, size = 'small', muted = false }) {
  const score = Number(value);
  return (
    <span className={`avaliacoes-stars avaliacoes-stars--${size} ${muted ? 'is-muted' : ''}`}>
      {[1, 2, 3, 4, 5].map((star) => (
        score >= star ? <StarRoundedIcon key={star} /> : <StarBorderRoundedIcon key={star} />
      ))}
    </span>
  );
}

function FilterField({ label, children, className = '' }) {
  return (
    <div className={`avaliacoes-filter-field ${className}`}>
      <Typography variant="body2" component="label">
        {label}
      </Typography>
      {children}
    </div>
  );
}

function renderSelectValue(options, placeholder, value) {
  if (!value) {
    return <span className="avaliacoes-select-placeholder">{placeholder}</span>;
  }
  return options.find((option) => option.value === value)?.label || value;
}

function renderScoreValue(value) {
  if (!value) {
    return <span className="avaliacoes-select-placeholder">Selecione a nota</span>;
  }
  return (
    <span className="avaliacoes-score-option">
      {value}
      <StarRating value={Number(value)} size="tiny" />
    </span>
  );
}

function RatingBreakdown({ distribution }) {
  const total = Object.values(distribution).reduce((sum, value) => sum + value, 0);

  return (
    <div className="avaliacoes-breakdown">
      <Typography variant="caption" className="avaliacoes-card-muted">
        Confira os detalhes das avaliações
      </Typography>
      {[5, 4, 3, 2, 1].map((score) => {
        const count = distribution[score] || 0;
        const width = total ? Math.round((count / total) * 100) : 0;
        return (
          <div key={score} className="avaliacoes-breakdown-row">
            <StarRating value={score} size="tiny" muted={count === 0} />
            <span className="avaliacoes-breakdown-count">{count}</span>
            <span className="avaliacoes-breakdown-bar">
              <span style={{ width: `${width}%` }} />
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ReviewIllustration({ variant = 'store' }) {
  return (
    <div className={`avaliacoes-illustration avaliacoes-illustration--${variant}`} aria-hidden="true">
      <span className="avaliacoes-illustration-orbit" />
      <span className="avaliacoes-illustration-head" />
      <span className="avaliacoes-illustration-body" />
      <span className="avaliacoes-illustration-arm" />
      <span className="avaliacoes-illustration-bag" />
      <span className="avaliacoes-illustration-star" />
    </div>
  );
}

function EmptyState({ section }) {
  return (
    <div className="avaliacoes-empty">
      <ReviewIllustration variant={section === 'logistics' ? 'delivery' : 'store'} />
      <Typography variant="h6">Nenhuma avaliação por enquanto</Typography>
      <Typography variant="body2" color="text.secondary">
        Assim que chegarem, você poderá acompanhar por aqui
      </Typography>
    </div>
  );
}

function PerformanceCard({ title, description, score, count, distribution, variant, period }) {
  const scoreLabel = formatScore(score);

  return (
    <section className="avaliacoes-section-block">
      <div className="avaliacoes-section-title">
        <Typography variant="h5" component="h2">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          nos últimos 90 dias
        </Typography>
      </div>
      <Typography variant="body2" className="avaliacoes-section-description">
        {description}
      </Typography>
      <div className="avaliacoes-performance-card">
        <div className="avaliacoes-score-column">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h3" component="p">
              {scoreLabel}
            </Typography>
            {scoreLabel === '--' ? <span className="avaliacoes-info-dot">!</span> : null}
          </Stack>
          <StarRating value={score || 0} muted={!score} />
          <Typography variant="caption" className="avaliacoes-card-muted">
            Período atual
            <br />
            {period}
          </Typography>
          <Typography variant="body2" className="avaliacoes-total-label">
            {count} avaliações {variant === 'delivery' ? 'sobre a entrega' : 'sobre a loja'}
          </Typography>
        </div>
        <RatingBreakdown distribution={distribution} />
        <ReviewIllustration variant={variant} />
      </div>
    </section>
  );
}

function Avaliacoes() {
  const auth = useAuth();
  const performanceFilters = useMemo(() => initialFilters(), []);
  const [mainTab, setMainTab] = useState('performance');
  const [reviewSection, setReviewSection] = useState('store');
  const [filters, setFilters] = useState(() => initialFilters());
  const [appliedFilters, setAppliedFilters] = useState(() => initialFilters());
  const [reviewPage, setReviewPage] = useState(1);
  const [showFilters, setShowFilters] = useState(true);
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchantId, setSelectedMerchantId] = useState('');
  const [merchantError, setMerchantError] = useState('');
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [summary, setSummary] = useState(null);
  const [performanceReviews, setPerformanceReviews] = useState({ store: [], logistics: [] });
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [performanceError, setPerformanceError] = useState('');
  const [reviewsPayload, setReviewsPayload] = useState({ reviews: [], total: 0, returnedCount: 0 });
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewsError, setReviewsError] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [detailMerchantId, setDetailMerchantId] = useState('');
  const [agentReviewContext, setAgentReviewContext] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [answerError, setAnswerError] = useState('');
  const [answerSuccess, setAnswerSuccess] = useState('');
  const [answerSending, setAnswerSending] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [agentConfig, setAgentConfig] = useState(() => defaultAgentConfig);
  const [agentTemplates, setAgentTemplates] = useState([]);
  const [agentModelLabel, setAgentModelLabel] = useState('');
  const [agentCanSave, setAgentCanSave] = useState(false);
  const [agentRiskKeywordsText, setAgentRiskKeywordsText] = useState('');
  const [agentStatus, setAgentStatus] = useState(null);
  const [agentLogs, setAgentLogs] = useState([]);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentSaving, setAgentSaving] = useState(false);
  const [agentRunning, setAgentRunning] = useState(false);
  const [agentError, setAgentError] = useState('');
  const [agentSuccess, setAgentSuccess] = useState('');

  const selectedMerchant = useMemo(
    () => merchants.find((merchant) => merchant.id === selectedMerchantId) || null,
    [merchants, selectedMerchantId]
  );
  const detailMerchant = useMemo(
    () => merchants.find((merchant) => merchant.id === detailMerchantId) || null,
    [detailMerchantId, merchants]
  );
  const canReplyToIfoodReviews = auth.isAuthenticated;
  const canSaveAgentConfig = agentCanSave && canManageAgentConfig(auth.user);

  const periodLabel = useMemo(() => (
    `${formatDate(toIsoStart(performanceFilters.dateFrom))} a ${formatDate(toIsoEnd(performanceFilters.dateTo))}`
  ), [performanceFilters]);

  const loadMerchants = useCallback(async () => {
    setLoadingMerchants(true);
    setMerchantError('');
    try {
      const response = await apiFetch(`${API_URL}/api/ifood/merchants`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Não foi possível carregar as lojas do iFood.');
      }
      const nextMerchants = normalizeMerchantList(data);
      setMerchants(nextMerchants);
      setSelectedMerchantId((current) => current || nextMerchants[0]?.id || '');
    } catch (err) {
      setMerchantError(err.message);
      setMerchants([]);
      setSelectedMerchantId('');
    } finally {
      setLoadingMerchants(false);
    }
  }, []);

  const loadPerformance = useCallback(async (merchantId) => {
    if (!merchantId) return;
    const performanceFilters = {
      ...initialFilters(),
      score: '',
      status: '',
      hasComments: '',
      orderId: '',
      sortBy: 'CREATED_AT',
      sort: 'DESC',
    };

    setLoadingPerformance(true);
    setPerformanceError('');
    try {
      const [summaryResponse, storeResponse] = await Promise.all([
        apiFetch(`${API_URL}/api/ifood/merchants/${merchantId}/reviews/summary`),
        apiFetch(buildReviewsUrl(merchantId, performanceFilters, 'store')),
      ]);

      const [summaryData, storeData] = await Promise.all([
        summaryResponse.json().catch(() => ({})),
        storeResponse.json().catch(() => ({})),
      ]);

      if (!summaryResponse.ok) throw new Error(summaryData.error || 'Falha ao carregar o desempenho das avaliações.');
      if (!storeResponse.ok) throw new Error(storeData.error || 'Falha ao carregar avaliações da loja.');

      setSummary(summaryData);
      setPerformanceReviews({
        store: Array.isArray(storeData.reviews) ? storeData.reviews : [],
        logistics: [],
      });
      setLastUpdatedAt(new Date().toISOString());
    } catch (err) {
      setPerformanceError(err.message);
    } finally {
      setLoadingPerformance(false);
    }
  }, []);

  const loadReviews = useCallback(async (
    merchantId = selectedMerchantId,
    section = reviewSection,
    nextFilters = appliedFilters,
    page = reviewPage
  ) => {
    if (!merchantId) return;
    setLoadingReviews(true);
    setReviewsError('');
    try {
      const response = await apiFetch(buildReviewsUrl(merchantId, nextFilters, section, { page }));
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao consultar avaliações.');
      }
      setReviewsPayload({
        ...data,
        reviews: Array.isArray(data.reviews) ? data.reviews : [],
      });
      setLastUpdatedAt(new Date().toISOString());
    } catch (err) {
      setReviewsError(err.message);
      setReviewsPayload({ reviews: [], total: 0, returnedCount: 0 });
    } finally {
      setLoadingReviews(false);
    }
  }, [appliedFilters, reviewPage, reviewSection, selectedMerchantId]);

  const loadAgentConfig = useCallback(async () => {
    setAgentLoading(true);
    setAgentError('');
    try {
      const response = await apiFetch(`${API_URL}/api/ifood/reviews/agent/config`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao carregar configurações do agente.');
      }
      const config = normalizeAgentConfig(data.config);
      setAgentConfig(config);
      setAgentTemplates(Array.isArray(data.templates) ? data.templates : []);
      setAgentModelLabel(data.modelLabel || '');
      setAgentCanSave(Boolean(data.canSave));
      setAgentRiskKeywordsText((config.riskKeywords || []).join('\n'));
    } catch (err) {
      setAgentError(err.message);
    } finally {
      setAgentLoading(false);
    }
  }, []);

  const loadAgentStatus = useCallback(async () => {
    try {
      const response = await apiFetch(`${API_URL}/api/ifood/reviews/agent/status`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao carregar status do agente.');
      }
      setAgentStatus(data);
    } catch (err) {
      setAgentError(err.message);
    }
  }, []);

  const loadAgentLogs = useCallback(async () => {
    try {
      const response = await apiFetch(`${API_URL}/api/ifood/reviews/agent/logs`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao carregar logs do agente.');
      }
      setAgentLogs(Array.isArray(data.logs) ? data.logs : []);
    } catch (err) {
      setAgentError(err.message);
    }
  }, []);

  useEffect(() => {
    loadMerchants();
  }, [loadMerchants]);

  useEffect(() => {
    if (!selectedMerchantId) return;
    loadPerformance(selectedMerchantId);
  }, [selectedMerchantId, loadPerformance]);

  useEffect(() => {
    if (!selectedMerchantId) return;
    loadReviews(selectedMerchantId, reviewSection);
  }, [selectedMerchantId, reviewPage, reviewSection, loadReviews]);

  useEffect(() => {
    if (mainTab !== 'agent') return;
    loadAgentConfig();
    loadAgentStatus();
    loadAgentLogs();
  }, [mainTab, loadAgentConfig, loadAgentLogs, loadAgentStatus]);

  const storeDistribution = useMemo(
    () => buildDistribution(performanceReviews.store),
    [performanceReviews.store]
  );

  const deliveryDistribution = useMemo(
    () => buildDistribution(performanceReviews.logistics),
    [performanceReviews.logistics]
  );

  const deliveryAverage = useMemo(
    () => calculateAverage(performanceReviews.logistics),
    [performanceReviews.logistics]
  );

  const irregularCount = useMemo(() => (
    reviewsPayload.reviews.filter((review) => ['INVALID', 'DISCARDED'].includes(String(review.status || '').toUpperCase())).length
  ), [reviewsPayload.reviews]);
  const pageCount = Number(reviewsPayload.pageCount || 0);
  const hasNextPage = pageCount ? reviewPage < pageCount : reviewsPayload.reviews.length >= (reviewSection === 'logistics' ? 10 : 50);
  const agentBlockers = useMemo(() => (
    Array.isArray(agentStatus?.blockers) ? agentStatus.blockers.filter(Boolean) : []
  ), [agentStatus]);
  const lastCompanyRun = agentStatus?.scheduler?.lastCompanyRun || null;

  const openReviewDetail = async (review, options = {}) => {
    const targetMerchantId = String(options.merchantId || selectedMerchantId || '').trim();
    if (!targetMerchantId) return;

    if (targetMerchantId !== selectedMerchantId) {
      setSelectedMerchantId(targetMerchantId);
    }
    setDetailMerchantId(targetMerchantId);
    setAgentReviewContext(options.agentLog || null);
    setSelectedReview(review);
    setDetailOpen(true);
    setDetailLoading(true);
    setAnswerError('');
    setAnswerSuccess('');
    setAnswerText(getMerchantReply(review)?.text || '');

    try {
      const response = await apiFetch(`${API_URL}/api/ifood/merchants/${targetMerchantId}/reviews/${review.id}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao carregar detalhes da avaliação.');
      }
      setSelectedReview(data);
      setAnswerText(getMerchantReply(data)?.text || '');
    } catch (err) {
      setAnswerError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSubmitAnswer = async () => {
    const targetMerchantId = detailMerchantId || selectedMerchantId;
    if (!selectedReview?.id || !targetMerchantId) return;
    if (!canReplyToIfoodReviews) {
      setAnswerError('Faça login com acesso operacional para responder avaliações no iFood.');
      return;
    }
    const text = answerText.trim();
    if (text.length < 10 || text.length > 300) {
      setAnswerError('A resposta deve ter entre 10 e 300 caracteres.');
      return;
    }

    const confirmed = window.confirm('Enviar esta resposta para a avaliação no iFood?');
    if (!confirmed) return;

    setAnswerSending(true);
    setAnswerError('');
    setAnswerSuccess('');
    try {
      const response = await apiFetch(
        `${API_URL}/api/ifood/merchants/${targetMerchantId}/reviews/${selectedReview.id}/answers`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao responder avaliação.');
      }
      await openReviewDetail(selectedReview, { merchantId: targetMerchantId, agentLog: agentReviewContext });
      setAnswerSuccess('Resposta enviada ao iFood.');
      await Promise.all([
        loadReviews(targetMerchantId, reviewSection),
        loadPerformance(targetMerchantId),
        loadAgentLogs(),
      ]);
    } catch (err) {
      setAnswerError(err.message);
    } finally {
      setAnswerSending(false);
    }
  };

  const handleGenerateAiSuggestion = async () => {
    const targetMerchantId = detailMerchantId || selectedMerchantId;
    if (!selectedReview?.id || !targetMerchantId) return;
    setAiSuggesting(true);
    setAnswerError('');
    setAnswerSuccess('');

    try {
      const response = await apiFetch(
        `${API_URL}/api/ifood/merchants/${targetMerchantId}/reviews/${selectedReview.id}/ai-suggestion`,
        { method: 'POST' }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao gerar sugestão com IA.');
      }
      if (data.requiresHumanReview) {
        setAnswerError('Esta avaliação exige revisão humana.');
        return;
      }
      const text = String(data.answerText || '').trim();
      if (text.length < 10 || text.length > 300) {
        throw new Error('A sugestão gerada não respeitou o limite de 10 a 300 caracteres.');
      }
      setAnswerText(text);
      setAnswerSuccess('Sugestão gerada com IA. Revise antes de enviar.');
    } catch (err) {
      setAnswerError(err.message);
    } finally {
      setAiSuggesting(false);
    }
  };

  const handleOpenAgentLogReview = (log) => {
    const merchantId = String(log?.merchantId || '').trim();
    const reviewId = String(log?.reviewId || '').trim();
    if (!merchantId || !reviewId) return;

    openReviewDetail({
      id: reviewId,
      status: log.reviewStatus || 'NOT_REPLIED',
      score: log.reviewScore,
      comment: log.reviewCommentPreview || '',
      createdAt: log.reviewCreatedAt || log.at,
      order: log.reviewOrderShortId ? { shortId: log.reviewOrderShortId } : null,
    }, {
      merchantId,
      agentLog: {
        ...log,
        merchantName: getAgentLogMerchantName(log, merchants),
      },
    });
  };

  const handleAgentFieldChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setAgentConfig((current) => ({ ...current, [field]: value }));
    setAgentSuccess('');
  };

  const handleAgentSwitchChange = (field) => (event) => {
    setAgentConfig((current) => ({ ...current, [field]: event.target.checked }));
    setAgentSuccess('');
  };

  const handleAgentTemplateSelect = (templateId) => {
    const selectedTemplate = agentTemplates.find((template) => template.id === templateId);
    setAgentConfig((current) => ({
      ...current,
      selectedTemplateId: templateId,
      tone: selectedTemplate?.defaultTone || current.tone || 'cordial',
    }));
    setAgentSuccess('');
  };

  const handleSaveAgentConfig = async () => {
    if (!canSaveAgentConfig) return;
    setAgentSaving(true);
    setAgentError('');
    setAgentSuccess('');

    const riskKeywords = agentRiskKeywordsText
      .split(/[\n,;]+/)
      .map((item) => item.trim())
      .filter(Boolean);

    try {
      const response = await apiFetch(`${API_URL}/api/ifood/reviews/agent/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: Boolean(agentConfig.enabled),
          autoReplyEnabled: Boolean(agentConfig.autoReplyEnabled),
          selectedTemplateId: agentConfig.selectedTemplateId,
          customInstructions: agentConfig.customInstructions,
          tone: agentConfig.tone,
          maxChars: Number(agentConfig.maxChars),
          replyWithoutComment: Boolean(agentConfig.replyWithoutComment),
          autoReplyOnlyStoreReviews: Boolean(agentConfig.autoReplyOnlyStoreReviews),
          maxRepliesPerRun: Number(agentConfig.maxRepliesPerRun),
          riskKeywords,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao salvar configurações do agente.');
      }
      const config = normalizeAgentConfig(data.config);
      setAgentConfig(config);
      setAgentTemplates(Array.isArray(data.templates) ? data.templates : agentTemplates);
      setAgentModelLabel(data.modelLabel || agentModelLabel);
      setAgentCanSave(Boolean(data.canSave));
      setAgentRiskKeywordsText((config.riskKeywords || []).join('\n'));
      setAgentSuccess('Configurações salvas.');
      await loadAgentStatus();
      await loadAgentLogs();
    } catch (err) {
      setAgentError(err.message);
    } finally {
      setAgentSaving(false);
    }
  };

  const handleRunAgentNow = async () => {
    if (!canSaveAgentConfig) return;
    setAgentRunning(true);
    setAgentError('');
    setAgentSuccess('');

    try {
      const response = await apiFetch(`${API_URL}/api/ifood/reviews/agent/run-now`, {
        method: 'POST',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao executar o agente.');
      }
      const summary = formatAgentRunSummary(data.result);
      if (data.success) {
        setAgentSuccess(summary ? `Ciclo executado: ${summary}.` : 'Ciclo executado.');
      } else {
        setAgentError(summary ? `Ciclo não executado: ${summary}.` : 'Ciclo não executado.');
      }
      setAgentStatus(data.status || null);
      await Promise.all([
        loadAgentLogs(),
        selectedMerchantId ? loadReviews(selectedMerchantId, reviewSection) : Promise.resolve(),
        selectedMerchantId ? loadPerformance(selectedMerchantId) : Promise.resolve(),
      ]);
    } catch (err) {
      setAgentError(err.message);
    } finally {
      setAgentRunning(false);
    }
  };

  const handleRefresh = () => {
    if (!selectedMerchantId) return;
    loadPerformance(selectedMerchantId);
    loadReviews(selectedMerchantId, reviewSection);
    if (mainTab === 'agent') {
      loadAgentConfig();
      loadAgentStatus();
      loadAgentLogs();
    }
  };

  const handleConsult = () => {
    setReviewPage(1);
    setAppliedFilters(filters);
  };

  const closeReviewDetail = () => {
    setDetailOpen(false);
    setAgentReviewContext(null);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters((current) => ({ ...current, [field]: event.target.value }));
  };

  return (
    <Box className="avaliacoes-page">
      <div className="avaliacoes-shell">
        <header className="avaliacoes-header">
          <div>
            <div className="avaliacoes-title-line">
              <PageMenuButton />
              <Typography variant="h4" component="h1">Avaliações</Typography>
            </div>
            {selectedMerchant ? (
              <Typography variant="body2" color="text.secondary">
                {selectedMerchant.displayName}
              </Typography>
            ) : null}
          </div>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
            {lastUpdatedAt ? (
              <Chip
                size="small"
                icon={<CalendarTodayRoundedIcon />}
                label={`Atualizado ${formatDateTime(lastUpdatedAt)}`}
                className="avaliacoes-soft-chip"
              />
            ) : null}
            <Button
              variant="text"
              className="avaliacoes-policy-link"
              href={REVIEW_POLICY_URL}
              target="_blank"
              rel="noreferrer"
            >
              Política de avaliação
            </Button>
            <Tooltip title="Atualizar avaliações">
              <span>
                <IconButton onClick={handleRefresh} disabled={!selectedMerchantId || loadingReviews || loadingPerformance}>
                  <RefreshRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </header>

        <div className="avaliacoes-top-tabs">
          <button
            type="button"
            className={mainTab === 'performance' ? 'is-active' : ''}
            onClick={() => setMainTab('performance')}
          >
            Desempenho
          </button>
          <button
            type="button"
            className={mainTab === 'reviews' ? 'is-active' : ''}
            onClick={() => setMainTab('reviews')}
          >
            Avaliações e comentários
          </button>
          <button
            type="button"
            className={mainTab === 'agent' ? 'is-active' : ''}
            onClick={() => setMainTab('agent')}
          >
            Agente de IA
          </button>
        </div>

        {loadingMerchants ? (
          <div className="avaliacoes-loading"><CircularProgress size={28} /></div>
        ) : null}

        {merchantError ? <Alert severity="error" sx={{ mb: 2 }}>{merchantError}</Alert> : null}

        {!loadingMerchants && !selectedMerchantId && !merchantError ? (
          <Alert severity="warning">Nenhuma loja iFood foi encontrada para este login.</Alert>
        ) : null}

        {selectedMerchantId && merchants.length > 1 ? (
          <FormControl size="small" className="avaliacoes-merchant-select">
            <InputLabel>Loja</InputLabel>
            <Select value={selectedMerchantId} label="Loja" onChange={(event) => setSelectedMerchantId(event.target.value)}>
              {merchants.map((merchant) => (
                <MenuItem key={merchant.id} value={merchant.id}>{merchant.displayName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        {mainTab === 'performance' && selectedMerchantId ? (
          <main className="avaliacoes-performance">
            {performanceError ? <Alert severity="error">{performanceError}</Alert> : null}
            {loadingPerformance ? <div className="avaliacoes-loading"><CircularProgress size={28} /></div> : null}
            <PerformanceCard
              title="Sobre a loja"
              description="As avaliações sobre a loja são as informações que aparecem no aplicativo do cliente, dentro do perfil da sua loja"
              score={summary?.score}
              count={Number(summary?.validReviewsCount || performanceReviews.store.length || 0)}
              distribution={storeDistribution}
              variant="store"
              period={periodLabel}
            />
            <PerformanceCard
              title="Sobre a entrega"
              description="As avaliações sobre a entrega são visíveis apenas para você, seus clientes não tem acesso a essas informações"
              score={deliveryAverage}
              count={performanceReviews.logistics.length}
              distribution={deliveryDistribution}
              variant="delivery"
              period={periodLabel}
            />
          </main>
        ) : null}

        {mainTab === 'reviews' && selectedMerchantId ? (
          <main className="avaliacoes-reviews">
            <Typography variant="h5" component="h2">Avaliações e comentários</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Escolha quais avaliações você deseja visualizar
            </Typography>

            <div className="avaliacoes-section-tabs">
              <button
                type="button"
                className={reviewSection === 'store' ? 'is-active' : ''}
                onClick={() => {
                  setReviewPage(1);
                  setReviewSection('store');
                }}
              >
                <StorefrontRoundedIcon />
                Avaliações da loja
              </button>
              <button
                type="button"
                className={reviewSection === 'logistics' ? 'is-active' : ''}
                onClick={() => {
                  setReviewPage(1);
                  setReviewSection('logistics');
                }}
              >
                <DeliveryDiningRoundedIcon />
                Avaliações da entrega
              </button>
            </div>

            <div className="avaliacoes-list-card">
              <div className="avaliacoes-list-header">
                <div>
                  <Typography variant="h6">
                    {reviewSection === 'logistics' ? 'Avaliações da entrega' : 'Avaliações da loja'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {reviewsPayload.returnedCount || reviewsPayload.reviews.length || 0} avaliações
                    {irregularCount ? `   ${irregularCount} irregulares` : '   0 irregulares'}
                  </Typography>
                </div>
                <Button
                  variant="text"
                  startIcon={<TuneRoundedIcon />}
                  className="avaliacoes-filter-toggle"
                  onClick={() => setShowFilters((current) => !current)}
                >
                  {showFilters ? 'Esconder filtros' : 'Mostrar filtros'}
                </Button>
              </div>

              {showFilters ? (
                <div className="avaliacoes-filters">
                  <FilterField label="Ordenar pela data da(o)">
                    <FormControl fullWidth size="small">
                      <Select
                        value={filters.sortBy}
                        onChange={handleFilterChange('sortBy')}
                        className="avaliacoes-filter-input"
                      >
                        {orderOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </FilterField>
                  <FilterField label="De">
                    <TextField
                      size="small"
                      type="date"
                      value={filters.dateFrom}
                      onChange={handleFilterChange('dateFrom')}
                      className="avaliacoes-filter-input"
                    />
                  </FilterField>
                  <FilterField label="Até">
                    <TextField
                      size="small"
                      type="date"
                      value={filters.dateTo}
                      onChange={handleFilterChange('dateTo')}
                      className="avaliacoes-filter-input"
                    />
                  </FilterField>
                  <FilterField label="Nota">
                    <FormControl fullWidth size="small">
                      <Select
                        value={filters.score}
                        onChange={handleFilterChange('score')}
                        displayEmpty
                        renderValue={(value) => renderScoreValue(value)}
                        className="avaliacoes-filter-input"
                      >
                        {scoreOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <span className="avaliacoes-score-option">
                              {option.label}
                              <StarRating value={Number(option.value)} size="tiny" />
                            </span>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </FilterField>
                  <FilterField label="Número do pedido">
                    <TextField
                      size="small"
                      placeholder="Digite o número do pedido"
                      value={filters.orderId}
                      onChange={handleFilterChange('orderId')}
                      className="avaliacoes-filter-input"
                    />
                  </FilterField>
                  {reviewSection === 'store' ? (
                    <FilterField label="Status da avaliação">
                      <FormControl fullWidth size="small">
                        <Select
                          value={filters.status}
                          onChange={handleFilterChange('status')}
                          displayEmpty
                          renderValue={(value) => renderSelectValue(statusOptions, 'Selecione o status', value)}
                          className="avaliacoes-filter-input"
                        >
                          {statusOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FilterField>
                  ) : null}
                  {reviewSection === 'store' ? (
                    <FilterField label="Possui comentários">
                      <FormControl fullWidth size="small">
                        <Select
                          value={filters.hasComments}
                          onChange={handleFilterChange('hasComments')}
                          displayEmpty
                          renderValue={(value) => renderSelectValue(commentOptions, 'Selecione uma opção', value)}
                          className="avaliacoes-filter-input"
                        >
                          {commentOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FilterField>
                  ) : null}
                  <Button
                    variant="outlined"
                    className="avaliacoes-consultar"
                    onClick={handleConsult}
                    disabled={loadingReviews}
                  >
                    Consultar
                  </Button>
                </div>
              ) : null}

              {reviewsError ? <Alert severity="error" sx={{ mt: 2 }}>{reviewsError}</Alert> : null}
              {loadingReviews ? <div className="avaliacoes-loading"><CircularProgress size={28} /></div> : null}

              {!loadingReviews && !reviewsPayload.reviews.length ? (
                <EmptyState section={reviewSection} />
              ) : null}

              {!loadingReviews && reviewsPayload.reviews.length ? (
                <div className="avaliacoes-table-wrap">
                  <table className="avaliacoes-table">
                    <thead>
                      <tr>
                        <th>Pedido</th>
                        <th>Data da avaliação</th>
                        <th>Data do pedido</th>
                        <th>Nota</th>
                        <th>Comentário</th>
                        <th>Status da avaliação</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {reviewsPayload.reviews.map((review) => (
                        <tr key={review.id} onClick={() => openReviewDetail(review)}>
                          <td>{getReviewOrderShortId(review)}</td>
                          <td>{formatDate(review.createdAt)}</td>
                          <td>{formatDate(review.order?.createdAt)}</td>
                          <td>
                            <span className="avaliacoes-score-cell">
                              {formatScore(review.score).replace(',0', '')}
                              <StarRoundedIcon />
                            </span>
                          </td>
                          <td className="avaliacoes-comment-cell">{review.comment || '-'}</td>
                          <td>
                            <span className={`avaliacoes-status ${getReviewStatusClassName(review.status)}`}>
                              {getReviewStatusLabel(review.status)}
                            </span>
                          </td>
                          <td>
                            <KeyboardArrowRightRoundedIcon className="avaliacoes-row-arrow" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="avaliacoes-pagination">
                    <Typography variant="caption" color="text.secondary">
                      Página {reviewPage}{pageCount ? ` de ${pageCount}` : ''}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={reviewPage <= 1 || loadingReviews}
                        onClick={() => setReviewPage((current) => Math.max(1, current - 1))}
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!hasNextPage || loadingReviews}
                        onClick={() => setReviewPage((current) => current + 1)}
                      >
                        Próxima
                      </Button>
                    </Stack>
                  </div>
                </div>
              ) : null}
            </div>
          </main>
        ) : null}

        {mainTab === 'agent' ? (
          <main className="avaliacoes-agent">
            <div className="avaliacoes-agent-heading">
              <div>
                <Typography variant="h5" component="h2">Agente de IA</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  Configuração do tenant para respostas assistidas e automáticas
                </Typography>
              </div>
              <Chip
                icon={<SmartToyRoundedIcon />}
                label={`Modelo do sistema: ${agentModelLabel || 'gpt-5-nano'}`}
                className="avaliacoes-soft-chip"
              />
            </div>

            {agentError ? <Alert severity="error" sx={{ mt: 2 }}>{agentError}</Alert> : null}
            {agentSuccess ? <Alert severity="success" sx={{ mt: 2 }}>{agentSuccess}</Alert> : null}
            {!canSaveAgentConfig ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Seu perfil pode visualizar esta configuração, mas não pode salvar alterações.
              </Alert>
            ) : null}
            {agentLoading ? <div className="avaliacoes-loading"><CircularProgress size={28} /></div> : null}

            <section className="avaliacoes-agent-status">
              <div className="avaliacoes-agent-status-head">
                <div>
                  <Typography variant="subtitle1">Saúde da automação</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {agentStatus?.ready ? 'Pronta para responder automaticamente' : 'Automação com bloqueios'}
                  </Typography>
                </div>
                <Chip
                  label={agentStatus?.scheduler?.started ? 'Scheduler ativo' : 'Scheduler parado'}
                  color={agentStatus?.scheduler?.started ? 'success' : 'warning'}
                  size="small"
                />
              </div>

              <div className="avaliacoes-agent-status-grid">
                <span>OpenAI: {agentStatus?.environment?.openaiConfigured ? 'configurada' : 'ausente'}</span>
                <span>Lojas configuradas: {Number(agentStatus?.access?.configuredMerchantCount || 0)}</span>
                <span>Lojas autorizadas: {Number(agentStatus?.access?.ownedMerchantCount || 0)}</span>
                <span>Último ciclo: {lastCompanyRun?.finishedAt ? formatDateTime(lastCompanyRun.finishedAt) : '-'}</span>
              </div>

              {agentBlockers.length ? (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  {agentBlockers.map(formatAgentBlocker).join(' • ')}
                </Alert>
              ) : null}

              {lastCompanyRun ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {formatAgentRunSummary(lastCompanyRun)}
                </Typography>
              ) : null}
            </section>

            <section className="avaliacoes-agent-panel">
              <div className="avaliacoes-agent-switches">
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(agentConfig.enabled)}
                      onChange={handleAgentSwitchChange('enabled')}
                      disabled={!canSaveAgentConfig || agentSaving}
                    />
                  )}
                  label="Agente ativo"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(agentConfig.autoReplyEnabled)}
                      onChange={handleAgentSwitchChange('autoReplyEnabled')}
                      disabled={!canSaveAgentConfig || agentSaving || !agentConfig.enabled}
                    />
                  )}
                  label="Responder Automaticamente"
                />
              </div>

              <Divider />

              <div>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Template</Typography>
                <div className="avaliacoes-template-grid">
                  {agentTemplates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      className={`avaliacoes-template-card ${agentConfig.selectedTemplateId === template.id ? 'is-selected' : ''}`}
                      onClick={() => handleAgentTemplateSelect(template.id)}
                      disabled={!canSaveAgentConfig || agentSaving}
                    >
                      <span>{template.name}</span>
                      <small>{template.description}</small>
                    </button>
                  ))}
                </div>
              </div>

              <div className="avaliacoes-agent-grid">
                <FormControl fullWidth size="small">
                  <InputLabel>Tom</InputLabel>
                  <Select
                    value={agentConfig.tone}
                    label="Tom"
                    onChange={handleAgentFieldChange('tone')}
                    disabled={!canSaveAgentConfig || agentSaving}
                  >
                    {toneOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  type="number"
                  label="Máximo de caracteres"
                  value={agentConfig.maxChars}
                  onChange={handleAgentFieldChange('maxChars')}
                  inputProps={{ min: 80, max: 300 }}
                  disabled={!canSaveAgentConfig || agentSaving}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Respostas por ciclo"
                  value={agentConfig.maxRepliesPerRun}
                  onChange={handleAgentFieldChange('maxRepliesPerRun')}
                  inputProps={{ min: 1, max: 10 }}
                  disabled={!canSaveAgentConfig || agentSaving}
                />
              </div>

              <TextField
                label="Preferências do tenant"
                value={agentConfig.customInstructions}
                onChange={handleAgentFieldChange('customInstructions')}
                multiline
                minRows={4}
                fullWidth
                inputProps={{ maxLength: 2000 }}
                disabled={!canSaveAgentConfig || agentSaving}
              />

              <div className="avaliacoes-agent-checks">
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={Boolean(agentConfig.replyWithoutComment)}
                      onChange={handleAgentFieldChange('replyWithoutComment')}
                      disabled={!canSaveAgentConfig || agentSaving}
                    />
                  )}
                  label="Responder avaliações sem comentário"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={Boolean(agentConfig.autoReplyOnlyStoreReviews)}
                      onChange={handleAgentFieldChange('autoReplyOnlyStoreReviews')}
                      disabled={!canSaveAgentConfig || agentSaving}
                    />
                  )}
                  label="Responder só avaliações da loja"
                />
              </div>

              <TextField
                label="Palavras de risco"
                value={agentRiskKeywordsText}
                onChange={(event) => {
                  setAgentRiskKeywordsText(event.target.value);
                  setAgentSuccess('');
                }}
                multiline
                minRows={3}
                fullWidth
                disabled={!canSaveAgentConfig || agentSaving}
              />

              <div className="avaliacoes-agent-actions">
                <Button
                  variant="contained"
                  onClick={handleSaveAgentConfig}
                  disabled={!canSaveAgentConfig || agentSaving}
                >
                  {agentSaving ? 'Salvando...' : 'Salvar configurações'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    loadAgentConfig();
                    loadAgentStatus();
                    loadAgentLogs();
                  }}
                  disabled={agentLoading || agentSaving || agentRunning}
                >
                  Atualizar logs
                </Button>
                <Button
                  variant="outlined"
                  startIcon={agentRunning ? <CircularProgress size={16} /> : <AutoAwesomeRoundedIcon />}
                  onClick={handleRunAgentNow}
                  disabled={!canSaveAgentConfig || agentLoading || agentSaving || agentRunning}
                >
                  {agentRunning ? 'Executando...' : 'Executar agora'}
                </Button>
              </div>
            </section>

            <section className="avaliacoes-agent-logs">
              <Typography variant="h6">Logs recentes</Typography>
              {!agentLogs.length ? (
                <Typography variant="body2" color="text.secondary">Nenhum log registrado.</Typography>
              ) : (
                <Stack spacing={1}>
                  {agentLogs.slice(0, 30).map((log, index) => (
                    <div key={`${log.at || index}-${log.reviewId || index}`} className="avaliacoes-agent-log">
                      <div className="avaliacoes-agent-log-head">
                        <strong>{getAgentLogStatusLabel(log)}</strong>
                        <span>{formatDateTime(log.at)}</span>
                      </div>
                      <Typography variant="body2">{log.reportMessage || log.message || '-'}</Typography>
                      <div className="avaliacoes-agent-log-meta">
                        {log.merchantId ? <span>Loja: {getAgentLogMerchantName(log, merchants)}</span> : null}
                        {log.reviewOrderShortId ? <span>Pedido: {log.reviewOrderShortId}</span> : null}
                        {Number.isFinite(Number(log.reviewScore)) ? <span>Nota: {Number(log.reviewScore).toLocaleString('pt-BR')}</span> : null}
                        {log.actionLabel ? <span>{log.actionLabel}</span> : null}
                      </div>
                      {canOpenAgentLogReview(log) ? (
                        <div className="avaliacoes-agent-log-actions">
                          <Button
                            size="small"
                            variant={isHumanReviewLog(log) ? 'contained' : 'outlined'}
                            onClick={() => handleOpenAgentLogReview(log)}
                          >
                            {isHumanReviewLog(log) ? 'Abrir avaliação e responder' : 'Abrir avaliação'}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </Stack>
              )}
            </section>
          </main>
        ) : null}
      </div>

      <Dialog open={detailOpen} onClose={closeReviewDetail} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <Typography variant="h6">Detalhes da avaliação</Typography>
              <Typography variant="body2" color="text.secondary">
                {detailMerchant?.displayName || agentReviewContext?.merchantName || selectedMerchant?.displayName || 'Loja iFood'} • Pedido {getReviewOrderShortId(selectedReview || {})}
              </Typography>
            </Box>
            <Chip label={getReviewStatusLabel(selectedReview?.status)} size="small" />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? <div className="avaliacoes-loading"><CircularProgress size={28} /></div> : null}
          {answerError ? <Alert severity="error" sx={{ mb: 2 }}>{answerError}</Alert> : null}
          {answerSuccess ? <Alert severity="success" sx={{ mb: 2 }}>{answerSuccess}</Alert> : null}
          {agentReviewContext ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Avaliação sinalizada pelo agente para revisão humana. Revise o comentário e envie a resposta manualmente para esta avaliação.
            </Alert>
          ) : null}

          {selectedReview ? (
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Typography variant="h4">{formatScore(selectedReview.score)}</Typography>
                <StarRating value={selectedReview.score || 0} />
                <Typography variant="body2" color="text.secondary">
                  {formatDateTime(selectedReview.createdAt)}
                </Typography>
              </Stack>

              <Box>
                <Typography variant="subtitle2">Comentário</Typography>
                <Typography variant="body1" className="avaliacoes-detail-comment">
                  {selectedReview.comment || 'Sem comentário.'}
                </Typography>
              </Box>

              {Array.isArray(selectedReview.questions) && selectedReview.questions.length ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Questionário</Typography>
                  <Stack spacing={1}>
                    {selectedReview.questions.map((question) => (
                      <div key={question.id || question.title} className="avaliacoes-question">
                        <Typography variant="body2" fontWeight={700}>{question.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(Array.isArray(question.answers) ? question.answers : [])
                            .map((answer) => answer.title)
                            .filter(Boolean)
                            .join(', ') || question.type || '-'}
                        </Typography>
                      </div>
                    ))}
                  </Stack>
                </Box>
              ) : null}

              {Array.isArray(selectedReview.replies) && selectedReview.replies.length ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Respostas</Typography>
                  <Stack spacing={1}>
                    {selectedReview.replies.map((reply, index) => (
                      <div key={`${reply.createdAt || index}-${reply.from || 'reply'}`} className="avaliacoes-reply">
                        <Typography variant="caption" color="text.secondary">
                          {reply.from === 'MERCHANT' ? 'Loja' : 'Cliente'} • {formatDateTime(reply.createdAt)}
                        </Typography>
                        <Typography variant="body2">{reply.text}</Typography>
                      </div>
                    ))}
                  </Stack>
                </Box>
              ) : null}

              <Divider />

              <Box>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ReplyRoundedIcon fontSize="small" color="error" />
                    <Typography variant="subtitle2">Responder pelo iFood</Typography>
                  </Stack>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AutoAwesomeRoundedIcon />}
                    onClick={handleGenerateAiSuggestion}
                    disabled={!selectedReview || !(detailMerchantId || selectedMerchantId) || !canReplyToIfoodReviews || !canAnswerReview(selectedReview) || aiSuggesting || answerSending}
                  >
                    {aiSuggesting ? 'Gerando...' : 'Gerar sugestão com IA'}
                  </Button>
                </Stack>
                <TextField
                  value={answerText}
                  onChange={(event) => setAnswerText(event.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                  placeholder="Escreva uma resposta entre 10 e 300 caracteres"
                  inputProps={{ maxLength: 300 }}
                  disabled={!canReplyToIfoodReviews || !canAnswerReview(selectedReview) || answerSending}
                  helperText={`${answerText.trim().length}/300 caracteres`}
                />
                {!canReplyToIfoodReviews ? (
                  <Typography variant="caption" color="text.secondary">
                    Faça login com acesso operacional para responder avaliações no iFood.
                  </Typography>
                ) : !canAnswerReview(selectedReview) ? (
                  <Typography variant="caption" color="text.secondary">
                    Apenas avaliações aguardando resposta aceitam envio. Respostas já enviadas só podem ser editadas por alguns minutos.
                  </Typography>
                ) : null}
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReviewDetail}>Fechar</Button>
          <Button
            variant="contained"
            onClick={handleSubmitAnswer}
            disabled={!selectedReview || !(detailMerchantId || selectedMerchantId) || !canReplyToIfoodReviews || !canAnswerReview(selectedReview) || answerSending}
          >
            {answerSending ? 'Enviando...' : 'Enviar resposta'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Avaliacoes;
